// src/screens/integrations/Integrations.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Facebook, Music2, ShoppingBag,
  RefreshCw, CheckCircle2, AlertCircle, ExternalLink, Search, Filter,
  Activity, Zap, Plus, X, Key, Loader2, User, Globe, Shield, Crown,
  MessageCircle, ArrowRight, Info, Store, Copy, Check, Code, Link as LinkIcon
} from 'lucide-react';
import './Integrations.css';
import { getApiUrl } from '../../config/api';

const getToken = () => localStorage.getItem('access_token') || '';

// Only attach Authorization header when a token actually exists.
const authHeaders = () => {
  const token = getToken();
  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const hasToken = () => Boolean(getToken());

const isDev = process.env.NODE_ENV !== 'production';
const dbg = (...args) => { if (isDev) console.log('[Integrations]', ...args); };

const DEFAULT_TIMEOUT_MS = 15000;

/**
 * Standardized API caller with timeout, auth token injection, and structured error handling.
 * Returns { ok: boolean, status: number, data: any, error: string|null }
 */
const apiCall = async (path, options = {}) => {
  const { timeout = DEFAULT_TIMEOUT_MS, headers = {}, ...restOptions } = options;
  const url = getApiUrl(path);

  if (!hasToken()) {
    dbg('apiCall skipped — no token:', path);
    return { ok: false, status: 0, data: null, error: 'Authentication required' };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url, {
      ...restOptions,
      signal: controller.signal,
      headers: {
        ...authHeaders(),
        ...headers,
      },
    });
    clearTimeout(timer);

    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }

    dbg(`apiCall ${res.status} ${path}`, data);
    return {
      ok: res.ok,
      status: res.status,
      data,
      error: res.ok ? null : (data?.detail || data?.message || `Request failed with status ${res.status}`)
    };
  } catch (err) {
    clearTimeout(timer);
    const isAbort = err.name === 'AbortError';
    const errorMsg = isAbort ? 'Request timed out after 15s. Please try again.' : (err.message || 'Network error');
    dbg(`apiCall network error on ${path}:`, errorMsg);
    return { ok: false, status: 0, data: null, error: errorMsg };
  }
};



/* ── Coming Soon overlay ──────────────────────────────────────────────────── */
const ComingSoonCard = ({ children }) => (
  <div style={{ position: 'relative', height: '100%' }}>
    <div style={{
      filter: 'grayscale(0.4) brightness(0.65)',
      pointerEvents: 'none', userSelect: 'none', height: '100%',
    }}>
      {children}
    </div>
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(10,11,20,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 'inherit', zIndex: 10,
    }}>
      <span style={{
        fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: '#ffffff', background: '#808080',
        border: '1px solid #808080', borderRadius: '999px', padding: '0.35rem 0.9rem',
        boxShadow: '0 2px 12px rgba(239,68,68,0.4)',
      }}>Coming Soon</span>
    </div>
  </div>
);



/* ── Leave Toast ──────────────────────────────────────────────────────────── */
const LeaveToast = ({ onDismiss }) => (
  <>
    <style>{`
      @keyframes intToastUp {
        from { opacity: 0; transform: translateX(-50%) translateY(24px); }
        to   { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `}</style>
    <div style={{
      position: 'fixed', bottom: '2rem', left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, background: 'rgba(20,21,35,0.97)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '1rem', padding: '1.25rem 1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      backdropFilter: 'blur(16px)', minWidth: '340px', maxWidth: '440px', width: '90vw',
      animation: 'intToastUp 0.35s cubic-bezier(0.16,1,0.3,1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
            background: 'rgba(0,178,255,0.12)', border: '1px solid rgba(0,178,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MessageCircle size={18} style={{ color: '#00B2FF' }} />
          </div>
          <div style={{
            width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
            background: 'rgba(149,191,71,0.12)', border: '1px solid rgba(149,191,71,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ShoppingBag size={18} style={{ color: '#95BF47' }} />
          </div>
          <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ffffff', margin: 0, lineHeight: 1.4 }}>
            Connect your store and DMs to unlock AI signals
          </h3>
        </div>
        <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: '2px', flexShrink: 0 }}>
          <X size={16} />
        </button>
      </div>
      <p style={{ fontSize: '0.9rem', color: '#6B7280', margin: '0 0 1rem', lineHeight: 1.6 }}>
        Revexia reads your ManyChat conversations and Shopify visits to show you which customers are most likely to buy right now.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={onDismiss} style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '0.4rem', padding: '0.65rem 1rem', background: '#3B82F6', borderRadius: '999px',
          fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#fff', border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(59,130,246,0.3)',
        }}>Set Up Integrations <ArrowRight size={13} /></button>
        <button onClick={onDismiss} style={{
          flex: 1, padding: '0.65rem 1rem', background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '999px',
          fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#9CA3AF', cursor: 'pointer',
        }}>Maybe Later</button>
      </div>
      <p style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: '#4B5563', letterSpacing: '0.04em', textAlign: 'center' }}>
        Follow the instructions sent via email
      </p>
    </div>
  </>
);



/* ── OAuth param extractor ────────────────────────────────────────────────── */
const extractOAuthParams = (popup) => {
  try {
    const url   = new URL(popup.location.href);
    const code  = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const error = url.searchParams.get('error');
    dbg('extractOAuthParams → code:', code ? code.slice(0, 20) + '…' : null, '| state:', state, '| error:', error);
    return { code, state, error };
  } catch {
    return { code: null, state: null, error: null };
  }
};

/* ── Format last synced timestamp ─────────────────────────────────────────── */
const formatLastSynced = (value) => {
  if (!value || value === 'NEVER') return 'Never';
  if (value === 'JUST NOW') return 'Just now';
  // ISO timestamp
  try {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      const diffMs  = Date.now() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1)  return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24)   return `${diffH}h ago`;
      return `${Math.floor(diffH / 24)}d ago`;
    }
  } catch {}
  return value;
};



/* ═══════════════════════════════════════════════════════════════════════════ */
const Integrations = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [visible,    setVisible]    = useState(false);
  const [showToast,  setShowToast]  = useState(false);

  // ── ManyChat state ────────────────────────────────────────────────────────
  const [showManyChatModal, setShowManyChatModal] = useState(false);
  const [apiKey,            setApiKey]            = useState('');
  const [isSaving,          setIsSaving]          = useState(false);
  const [saveError,         setSaveError]         = useState('');
  const [accountData,       setAccountData]       = useState(null);
  const [step,              setStep]              = useState('form');
  const [manyChatConnected, setManyChatConnected] = useState(false);
  const [manyChatAccount,   setManyChatAccount]   = useState(null);
  const [manyChatEvents,    setManyChatEvents]    = useState(0);
  const [manyChatLastSync,  setManyChatLastSync]  = useState('Never');

  // ── Facebook state ────────────────────────────────────────────────────────
  const [showFbModal,     setShowFbModal]     = useState(false);
  const [fbConnected,     setFbConnected]     = useState(false);
  const [fbConnecting,    setFbConnecting]    = useState(false);
  const [fbDisconnecting, setFbDisconnecting] = useState(false);
  const [fbError,         setFbError]         = useState('');
  const [metaApiAlert,    setMetaApiAlert]    = useState('');
  const [fbAccount,       setFbAccount]       = useState(null);
  const [fbEvents,        setFbEvents]        = useState(0);
  const [fbLastSync,      setFbLastSync]      = useState('Never');
  const oauthPopupRef  = useRef(null);
  const fbPollTimerRef = useRef(null);
  const fbChannelRef   = useRef(null);

  // ── Instagram state ───────────────────────────────────────────────────────
  const [showIgModal,     setShowIgModal]     = useState(false);
  const [igConnected,     setIgConnected]     = useState(false);
  const [igConnecting,    setIgConnecting]    = useState(false);
  const [igDisconnecting, setIgDisconnecting] = useState(false);
  const [igError,         setIgError]         = useState('');
  const [igAccount,       setIgAccount]       = useState(null);
  const [igEvents,        setIgEvents]        = useState(0);
  const [igLastSync,      setIgLastSync]      = useState('Never');
  const igOauthPopupRef  = useRef(null);
  const igPollTimerRef   = useRef(null);
  const igChannelRef     = useRef(null);
  const igExchangingRef  = useRef(false);

  // ── Shopify state ─────────────────────────────────────────────────────────
  const [shopifyConnected,     setShopifyConnected]     = useState(false);
  const [shopifyEvents,        setShopifyEvents]        = useState(0);
  const [shopifyLastSync,      setShopifyLastSync]      = useState('Never');
  const [shopifyAccount,       setShopifyAccount]       = useState(null);
  const [showShopifyModal,     setShowShopifyModal]     = useState(false);
  const [shopifyStep,          setShopifyStep]          = useState('install');
  const [shopifyGenerating,    setShopifyGenerating]    = useState(false);
  const [shopifyError,         setShopifyError]         = useState('');
  const [shopifySnippet,       setShopifySnippet]       = useState('');
  const [shopifySiteId,        setShopifySiteId]        = useState('');
  const [shopifyDomain,        setShopifyDomain]        = useState('');
  const [shopifyCopied,        setShopifyCopied]        = useState(false);
  const [shopifyVerifying,     setShopifyVerifying]     = useState(false);
  const [shopifyDisconnecting, setShopifyDisconnecting] = useState(false);

  // ── TikTok state ──────────────────────────────────────────────────────────
  const [tiktokConnected,     setTiktokConnected]     = useState(false);
  const [tiktokEvents,        setTiktokEvents]        = useState(0);
  const [tiktokLastSync,      setTiktokLastSync]      = useState('Never');
  const [tiktokAccount,       setTiktokAccount]       = useState(null);
  const [showTiktokModal,     setShowTiktokModal]     = useState(false);
  const [tiktokDisconnecting, setTiktokDisconnecting] = useState(false);
  const [tiktokConnecting,    setTiktokConnecting]    = useState(false);
  const [tiktokError,         setTiktokError]         = useState('');
  const [tiktokCopied,        setTiktokCopied]        = useState(false);
  const [tiktokTab,           setTiktokTab]           = useState('webhook'); // 'webhook' | 'api'
  const [tiktokAccessToken,   setTiktokAccessToken]   = useState('');
  const [tiktokPixelId,       setTiktokPixelId]       = useState('');
  const [tiktokAppId,         setTiktokAppId]         = useState('');

  // ── WooCommerce state ─────────────────────────────────────────────────────
  const [wooConnected,       setWooConnected]       = useState(false);
  const [wooEvents,          setWooEvents]          = useState(0);
  const [wooLastSync,        setWooLastSync]        = useState('Never');
  const [wooAccount,         setWooAccount]         = useState(null);
  const [showWooModal,       setShowWooModal]       = useState(false);
  const [wooStep,            setWooStep]            = useState('snippet'); // 'snippet' | 'api' | 'success'
  const [wooSiteId,          setWooSiteId]          = useState('');
  const [wooSnippet,         setWooSnippet]         = useState('');
  const [wooCopied,          setWooCopied]          = useState(false);
  const [wooGenerating,      setWooGenerating]      = useState(false);
  const [wooVerifying,       setWooVerifying]       = useState(false);
  const [wooDisconnecting,   setWooDisconnecting]   = useState(false);
  const [wooConnecting,      setWooConnecting]      = useState(false);
  const [wooError,           setWooError]           = useState('');
  const [wooDomain,          setWooDomain]          = useState('');
  const [wooConsumerKey,     setWooConsumerKey]     = useState('');
  const [wooConsumerSecret,  setWooConsumerSecret]  = useState('');

  // ── Entrance animation ────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  // ── Leave toast ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (manyChatConnected) return;
    const t = setTimeout(() => setShowToast(true), 4000);
    return () => clearTimeout(t);
  }, [manyChatConnected]);

  useEffect(() => {
    if (manyChatConnected) return;
    const handler = () => setShowToast(true);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [manyChatConnected]);


  // ── ManyChat status check (uses /manychat/status) ─────────────────────────
  const fetchManyChatAccount = useCallback(async () => {
    const { ok, data } = await apiCall('/manychat/account');
    console.log('[API] GET /manychat/account →', { ok, body: data });
    if (ok && data?.data) {
      setManyChatAccount(data.data);
      return data.data;
    }
    return null;
  }, []);

  const checkManyChatStatus = useCallback(async () => {
    const { ok, status, data } = await apiCall('/manychat/status');
    console.log('[API] GET /manychat/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      dbg('ManyChat connected — events:', data.events_tracked);
      setManyChatConnected(true);
      setManyChatEvents(data.events_tracked ?? 0);
      setManyChatLastSync(formatLastSynced(data.last_synced));
      fetchManyChatAccount();
      return { connected: true, data };
    } else {
      dbg('ManyChat not connected — status:', status, '| data.status:', data?.status);
      setManyChatConnected(false);
      setManyChatAccount(null);
      setManyChatEvents(0);
      setManyChatLastSync('Never');
      return { connected: false, data: null };
    }
  }, [fetchManyChatAccount]);

  // ── Facebook status check ─────────────────────────────────────────────────
  const checkFbStatus = useCallback(async () => {
    const { ok, status, data, error: apiErr } = await apiCall('/facebook/status');
    console.log('[API] GET /facebook/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      const name = data?.page_name || data?.page || data?.name || null;
      dbg('FB connected — events:', data.events_tracked, '| page:', name);
      setFbConnected(true);
      setFbEvents(data.events_tracked ?? 0);
      setFbLastSync(formatLastSynced(data.last_synced));
      setFbAccount(name ? { page: name } : null);
      setMetaApiAlert('');
      return { connected: true, data };
    } else {
      dbg('FB not connected — status:', status, '| data.status:', data?.status);
      setFbConnected(false);
      setFbAccount(null);
      setFbEvents(0);
      setFbLastSync('Never');
      if (!ok && (status >= 500 || status === 0 || apiErr?.toLowerCase().includes('meta') || apiErr?.toLowerCase().includes('facebook'))) {
        setMetaApiAlert('Facebook API upgrade is in progress');
      }
      return { connected: false, data: null };
    }
  }, []);

  // ── Instagram status check ────────────────────────────────────────────────
  const checkIgStatus = useCallback(async () => {
    const { ok, status, data } = await apiCall('/instagram/status');
    console.log('[API] GET /instagram/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      const name = data?.username || data?.page || data?.name || null;
      dbg('IG connected — events:', data.events_tracked, '| handle:', name);
      setIgConnected(true);
      setIgEvents(data.events_tracked ?? 0);
      setIgLastSync(formatLastSynced(data.last_synced));
      setIgAccount(name ? { page: name } : null);
      return { connected: true, data };
    } else {
      dbg('IG not connected — status:', status, '| data.status:', data?.status);
      setIgConnected(false);
      setIgAccount(null);
      setIgEvents(0);
      setIgLastSync('Never');
      return { connected: false, data: null };
    }
  }, []);

  // ── Shopify status check ──────────────────────────────────────────────────
  const checkShopifyStatus = useCallback(async () => {
    const { ok, status, data } = await apiCall('/shopify/status');
    console.log('[API] GET /shopify/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      dbg('Shopify connected — events:', data.events_tracked, '| domain:', data.domain);
      setShopifyConnected(true);
      setShopifyEvents(data.events_tracked ?? 0);
      setShopifyLastSync(formatLastSynced(data.last_synced));
      setShopifyAccount({ domain: data.domain, siteId: data.site_id, snippet: data.snippet });
      if (data.snippet) setShopifySnippet(data.snippet);
      if (data.site_id) setShopifySiteId(data.site_id);
      if (data.domain) setShopifyDomain(data.domain);
      return { connected: true, data };
    } else {
      dbg('Shopify not connected — status:', status, '| data.status:', data?.status);
      setShopifyConnected(false);
      setShopifyAccount(null);
      setShopifyEvents(0);
      setShopifyLastSync('Never');
      return { connected: false, data: null };
    }
  }, []);

  // ── TikTok status check ───────────────────────────────────────────────────
  const checkTiktokStatus = useCallback(async () => {
    const { ok, status, data } = await apiCall('/tiktok/status');
    console.log('[API] GET /tiktok/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      dbg('TikTok connected — events:', data.events_tracked);
      setTiktokConnected(true);
      setTiktokEvents(data.events_tracked ?? 0);
      setTiktokLastSync(formatLastSynced(data.last_synced));
      setTiktokAccount(data.account || { name: data.username || 'TikTok Account' });
      return { connected: true, data };
    } else {
      dbg('TikTok not connected — status:', status, '| data.status:', data?.status);
      setTiktokConnected(false);
      setTiktokAccount(null);
      setTiktokEvents(0);
      setTiktokLastSync('Never');
      return { connected: false, data: null };
    }
  }, []);

  // ── WooCommerce status check ──────────────────────────────────────────────
  const checkWooStatus = useCallback(async () => {
    const { ok, status, data } = await apiCall('/woocommerce/status');
    console.log('[API] GET /woocommerce/status →', { httpStatus: status, ok, body: data });
    if (ok && data?.status === 'CONNECTED') {
      dbg('WooCommerce connected — events:', data.events_tracked);
      setWooConnected(true);
      setWooEvents(data.events_tracked ?? 0);
      setWooLastSync(formatLastSynced(data.last_synced));
      setWooAccount({
        domain: data.domain || data.store_url || '',
        siteId: data.site_id || '',
        snippet: data.snippet || ''
      });
      if (data.snippet) setWooSnippet(data.snippet);
      if (data.site_id) setWooSiteId(data.site_id);
      if (data.domain || data.store_url) setWooDomain(data.domain || data.store_url);
      return { connected: true, data };
    } else {
      dbg('WooCommerce not connected — status:', status, '| data.status:', data?.status);
      setWooConnected(false);
      setWooAccount(null);
      setWooEvents(0);
      setWooLastSync('Never');
      return { connected: false, data: null };
    }
  }, []);

  // ── Refresh all statuses together on mount / reload ──────────────────────
  const refreshAllStatuses = useCallback(async () => {
    if (!hasToken()) {
      dbg('No token — skipping status checks');
      return;
    }
    await Promise.allSettled([
      checkManyChatStatus(),
      checkFbStatus(),
      checkIgStatus(),
      checkShopifyStatus(),
      checkTiktokStatus(),
      checkWooStatus(),
    ]);
  }, [checkManyChatStatus, checkFbStatus, checkIgStatus, checkShopifyStatus, checkTiktokStatus, checkWooStatus]);

  // ── On mount: load all existing statuses ─────────────────────────────────
  useEffect(() => {
    refreshAllStatuses();

    try {
      const params = new URLSearchParams(window.location.search);
      const errorParam = params.get('error') || params.get('error_description');
      const stateParam = params.get('state') || '';
      if (errorParam && (stateParam.includes('facebook') || stateParam.includes('meta') || stateParam.includes('instagram'))) {
        setMetaApiAlert('Facebook API upgrade is in progress');
        setFbError('Facebook API upgrade is in progress');
      }
    } catch {}

    return () => {
      if (fbPollTimerRef.current) clearInterval(fbPollTimerRef.current);
      if (igPollTimerRef.current) clearInterval(igPollTimerRef.current);
      if (fbChannelRef.current) { try { fbChannelRef.current.close(); } catch {} }
      if (igChannelRef.current) { try { igChannelRef.current.close(); } catch {} }
    };
  }, [refreshAllStatuses]);

  // ── Shopify handlers ──────────────────────────────────────────────────────
  const handleOpenShopifyModal = () => {
    setShopifyError('');
    setShopifyCopied(false);
    if (shopifyConnected && shopifyAccount) {
      setShopifyStep('success');
      if (shopifyAccount.snippet) setShopifySnippet(shopifyAccount.snippet);
      if (shopifyAccount.siteId)  setShopifySiteId(shopifyAccount.siteId);
      if (shopifyAccount.domain)  setShopifyDomain(shopifyAccount.domain);
    } else {
      setShopifyStep('install');
      if (!shopifySiteId) handleShopifyGenerateId();
    }
    setShowShopifyModal(true);
  };

  const handleCloseShopifyModal = () => {
    setShowShopifyModal(false);
    setShopifyError('');
    setShopifyCopied(false);
    setShopifyVerifying(false);
  };

  const handleShopifyGenerateId = async () => {
    setShopifyGenerating(true);
    setShopifyError('');
    try {
      const { ok, status, data, error } = await apiCall('/shopify/generate-id', { method: 'POST' });
      console.log('[API] POST /shopify/generate-id →', { httpStatus: status, ok, body: data });
      if (!ok) throw new Error(error || `Server error ${status}`);
      const siteId  = data?.site_id  || data?.siteId  || data?.id  || '';
      const snippet = data?.snippet  || data?.code    || '';
      if (!siteId) throw new Error('No site ID returned from server.');
      setShopifySiteId(siteId);
      setShopifySnippet(snippet || buildSnippet(siteId));
    } catch (err) {
      setShopifyError(err.message || 'Failed to generate site ID. Please try again.');
    } finally {
      setShopifyGenerating(false);
    }
  };

  const handleShopifyCopySnippet = () => {
    const text = shopifySnippet || buildSnippet(shopifySiteId);
    navigator.clipboard.writeText(text).then(() => {
      setShopifyCopied(true);
      setTimeout(() => setShopifyCopied(false), 2500);
    });
  };

  const buildSnippet = (siteId) =>
    `<!-- Revexia Configuration -->
<script>
  window.RevexiaTrackerConfig = {
    siteId: "${siteId}"
  };
</script>

<!-- Revexia Tracker via CDN -->
<script src="https://cdn.jsdelivr.net/gh/Software-Bari/Revexia-CDN@main/revexia-tracker.js" async></script>`;

  const handleShopifyVerify = async () => {
    setShopifyVerifying(true);
    setShopifyError('');
    try {
      const res = await checkShopifyStatus();
      if (res.connected) {
        setShopifyStep('success');
      } else {
        if (shopifyDomain.trim()) {
          const connectRes = await apiCall('/shopify/connect', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ domain: shopifyDomain.trim(), site_id: shopifySiteId })
          });
          if (connectRes.ok) {
            setShopifyConnected(true);
            setShopifyStep('success');
            await checkShopifyStatus();
            return;
          }
        }
        setShopifyError('Snippet not detected yet. Make sure it is pasted into your Shopify theme and saved.');
      }
    } catch {
      setShopifyError('Verification failed. Please try again.');
    } finally {
      setShopifyVerifying(false);
    }
  };

  const handleShopifyDisconnect = async () => {
    setShopifyDisconnecting(true);
    setShopifyError('');
    await apiCall('/shopify/disconnect', { method: 'POST' });
    setShopifyConnected(false);
    setShopifyAccount(null);
    setShopifyEvents(0);
    setShopifyLastSync('Never');
    setShopifySnippet('');
    setShopifySiteId('');
    setShopifyDomain('');
    setShopifyStep('install');
    setShopifyDisconnecting(false);
    setShowShopifyModal(false);
  };

  // ── WooCommerce handlers ──────────────────────────────────────────────────
  const handleOpenWooModal = () => {
    setWooError('');
    setWooCopied(false);
    if (wooConnected && wooAccount) {
      setWooStep('success');
      if (wooAccount.snippet) setWooSnippet(wooAccount.snippet);
      if (wooAccount.siteId)  setWooSiteId(wooAccount.siteId);
      if (wooAccount.domain)  setWooDomain(wooAccount.domain);
    } else {
      setWooStep('snippet');
      if (!wooSiteId) handleWooGenerateId();
    }
    setShowWooModal(true);
  };

  const handleCloseWooModal = () => {
    setShowWooModal(false);
    setWooError('');
    setWooCopied(false);
    setWooVerifying(false);
    setWooConnecting(false);
  };

  const handleWooGenerateId = async () => {
    setWooGenerating(true);
    setWooError('');
    try {
      const { ok, data } = await apiCall('/woocommerce/generate-id', { method: 'POST' });
      const siteId  = data?.site_id  || data?.siteId  || data?.id  || '';
      const snippet = data?.snippet  || data?.code    || '';
      if (ok && siteId) {
        setWooSiteId(siteId);
        setWooSnippet(snippet || buildWooSnippet(siteId));
      } else {
        const fallbackId = 'woo_' + Math.random().toString(36).substring(2, 10);
        setWooSiteId(fallbackId);
        setWooSnippet(buildWooSnippet(fallbackId));
      }
    } catch {
      const fallbackId = 'woo_' + Math.random().toString(36).substring(2, 10);
      setWooSiteId(fallbackId);
      setWooSnippet(buildWooSnippet(fallbackId));
    } finally {
      setWooGenerating(false);
    }
  };

  const buildWooSnippet = (siteId) =>
    `<!-- Revexia WooCommerce Tracker -->
<script>
  window.RevexiaTrackerConfig = {
    platform: "woocommerce",
    siteId: "${siteId}"
  };
</script>

<!-- Revexia Tracker via CDN -->
<script src="https://cdn.jsdelivr.net/gh/Software-Bari/Revexia-CDN@main/revexia-tracker.js" async></script>`;

  const handleWooCopySnippet = () => {
    const text = wooSnippet || buildWooSnippet(wooSiteId);
    navigator.clipboard.writeText(text).then(() => {
      setWooCopied(true);
      setTimeout(() => setWooCopied(false), 2500);
    });
  };

  const handleWooVerifySnippet = async () => {
    setWooVerifying(true);
    setWooError('');
    try {
      const res = await checkWooStatus();
      if (res.connected) {
        setWooStep('success');
      } else {
        const connectRes = await apiCall('/woocommerce/connect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ site_id: wooSiteId, domain: wooDomain, method: 'snippet' })
        });
        if (connectRes.ok) {
          setWooConnected(true);
          setWooAccount({ siteId: wooSiteId, domain: wooDomain, snippet: wooSnippet });
          setWooStep('success');
          await checkWooStatus();
        } else {
          setWooError('Snippet not detected on your WooCommerce store yet. Make sure it is placed in header.php or your tracking plugin.');
        }
      }
    } catch {
      setWooError('Verification failed. Please try again.');
    } finally {
      setWooVerifying(false);
    }
  };

  const handleWooConnectApi = async (e) => {
    e?.preventDefault();
    if (!wooDomain.trim()) {
      setWooError('Please enter your WooCommerce store URL.');
      return;
    }
    if (!wooConsumerKey.trim() || !wooConsumerSecret.trim()) {
      setWooError('Please enter both Consumer Key and Consumer Secret.');
      return;
    }
    setWooConnecting(true);
    setWooError('');
    try {
      const { ok, data, error } = await apiCall('/woocommerce/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_url: wooDomain.trim(),
          consumer_key: wooConsumerKey.trim(),
          consumer_secret: wooConsumerSecret.trim(),
          method: 'rest_api'
        })
      });
      if (!ok) throw new Error(error || 'Failed to connect WooCommerce API. Please check your credentials.');
      setWooConnected(true);
      setWooAccount({
        domain: wooDomain.trim(),
        siteId: data?.site_id || wooSiteId,
        ...data
      });
      setWooStep('success');
      await checkWooStatus();
    } catch (err) {
      setWooError(err.message || 'Connection failed. Verify your store URL and keys.');
    } finally {
      setWooConnecting(false);
    }
  };

  const handleWooDisconnect = async () => {
    setWooDisconnecting(true);
    setWooError('');
    await apiCall('/woocommerce/disconnect', { method: 'POST' });
    setWooConnected(false);
    setWooAccount(null);
    setWooEvents(0);
    setWooLastSync('Never');
    setWooSnippet('');
    setWooSiteId('');
    setWooDomain('');
    setWooConsumerKey('');
    setWooConsumerSecret('');
    setWooStep('snippet');
    setWooDisconnecting(false);
    setShowWooModal(false);
  };



  /* ── OAuth callback helpers ───────────────────────────────────────────────
     IMPORTANT: The Instagram /login endpoint returns a redirect_uri pointing to
     /facebook/callback (shared callback route) with state=instagram.
     So the IG token exchange must POST to /facebook/callback, not /instagram/callback.
  ─────────────────────────────────────────────────────────────────────────── */
  const callFbCallback = async (code, state = 'facebook') => {
    const path = `/facebook/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    dbg('callFbCallback →', path.replace(code, code.slice(0, 20) + '…'));
    const { ok, data } = await apiCall(path);
    if (ok) {
      const pageName = data?.page_name || data?.page || data?.name || data?.data?.page_name || data?.data?.name || null;
      return { success: true, pageName };
    }
    const errText = 'Facebook API upgrade is in progress';
    setMetaApiAlert(errText);
    return { success: false, error: errText };
  };

  // Instagram uses /facebook/callback with state=instagram (shared OAuth callback route)
  const callIgCallback = async (code, state = 'instagram') => {
    const path = `/facebook/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    dbg('callIgCallback (via /facebook/callback) →', path.replace(code, code.slice(0, 20) + '…'));
    const { ok, data } = await apiCall(path);
    if (ok) {
      const pageName = data?.username || data?.page || data?.name || data?.data?.username || data?.data?.name || null;
      return { success: true, pageName };
    }
    const errText = 'Facebook API upgrade is in progress';
    setMetaApiAlert(errText);
    return { success: false, error: errText };
  };



  // ── ManyChat handlers ─────────────────────────────────────────────────────
  const handleOpenModal = () => {
    if (manyChatConnected && manyChatAccount) { setAccountData(manyChatAccount); setStep('success'); }
    else setStep('form');
    setShowManyChatModal(true);
  };

  const handleCloseModal = () => {
    setShowManyChatModal(false);
    setApiKey('');
    setSaveError('');
    if (!manyChatConnected) { setAccountData(null); setStep('form'); }
  };

  const handleManyChatSubmit = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setIsSaving(true);
    setSaveError('');
    try {
      const postRes = await apiCall('/manychat/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey.trim() }),
      });
      if (!postRes.ok) {
        throw new Error(postRes.error || `Server error ${postRes.status}`);
      }
      // Re-check status to get events_tracked / last_synced
      await checkManyChatStatus();
      // Also try to get account detail for modal
      const accRes = await apiCall('/manychat/account');
      if (accRes.ok && accRes.data?.data) {
        const acc = accRes.data.data;
        setAccountData(acc);
        setManyChatAccount(acc);
      }
      setManyChatConnected(true);
      setStep('success');
      setShowToast(false);
    } catch (err) {
      setSaveError(err.message || 'Something went wrong. Check your API key.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await apiCall('/manychat/disconnect', { method: 'POST' });
    } catch {}
    setManyChatConnected(false);
    setManyChatAccount(null);
    setManyChatEvents(0);
    setManyChatLastSync('Never');
    setAccountData(null);
    setApiKey('');
    setSaveError('');
    setStep('form');
    setShowManyChatModal(false);
  };



  // ── Facebook OAuth handlers ───────────────────────────────────────────────
  const handleOpenFbModal  = () => {
    if (metaApiAlert) {
      setFbError(metaApiAlert);
    } else {
      setFbError('');
    }
    setShowFbModal(true);
  };

  const handleCloseFbModal = () => {
    if (fbPollTimerRef.current) { clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null; }
    if (fbChannelRef.current)   { try { fbChannelRef.current.close(); } catch {} fbChannelRef.current = null; }
    if (oauthPopupRef.current && !oauthPopupRef.current.closed) oauthPopupRef.current.close();
    setFbConnecting(false);
    setFbError('');
    setShowFbModal(false);
  };

  const handleFbOAuth = async () => {
    setFbConnecting(true);
    setFbError('');
    if (fbPollTimerRef.current) clearInterval(fbPollTimerRef.current);
    if (fbChannelRef.current)   { try { fbChannelRef.current.close(); } catch {} }

    try {
      const { ok, status, data } = await apiCall('/facebook/login');
      dbg('FB /login status:', status);
      if (!ok) {
        const msg = 'Facebook API upgrade is in progress';
        setFbError(msg);
        setMetaApiAlert(msg);
        throw new Error(msg);
      }
      const url = data?.url;
      if (!url) {
        const msg = 'Facebook API upgrade is in progress';
        setFbError(msg);
        setMetaApiAlert(msg);
        throw new Error(msg);
      }

      const popup = window.open(url, 'fb_oauth_popup', 'width=600,height=700,scrollbars=yes,resizable=yes');
      oauthPopupRef.current = popup;
      if (!popup) throw new Error('Popup was blocked. Please allow popups for this site and try again.');
      dbg('FB popup opened');

      let handled = false;

      // Strategy A: BroadcastChannel
      try {
        const ch = new BroadcastChannel('oauth_channel');
        fbChannelRef.current = ch;
        ch.onmessage = async (event) => {
          dbg('BroadcastChannel FB message:', event.data);
          const platform = (event.data?.platform || '').toLowerCase();
          if (platform && platform !== 'facebook') return;

          handled = true;
          ch.close(); fbChannelRef.current = null;
          clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null;

          const { type, code, state } = event.data;
          if (type === 'OAUTH_SUCCESS' || type === 'oauth_success' || type === 'success') {
            if (code) {
              const res = await callFbCallback(code, state || 'facebook');
              if (!res.success) {
                const msg = 'Facebook API upgrade is in progress';
                setFbError(msg);
                setMetaApiAlert(msg);
              }
            }
            await checkFbStatus();
            setFbConnecting(false);
            setShowFbModal(false);
          } else {
            setFbConnecting(false);
            const msg = 'Facebook API upgrade is in progress';
            setFbError(msg);
            setMetaApiAlert(msg);
          }
        };
      } catch (chErr) {
        dbg('BroadcastChannel not supported:', chErr.message);
      }

      // Strategy B: Poll popup URL
      fbPollTimerRef.current = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null;
          if (handled) return;
          if (fbChannelRef.current) { try { fbChannelRef.current.close(); } catch {} fbChannelRef.current = null; }
          dbg('FB popup closed — re-checking status via API');
          const st = await checkFbStatus();
          setFbConnecting(false);
          if (!st?.connected) {
            const msg = 'Facebook API upgrade is in progress';
            setFbError(msg);
            setMetaApiAlert(msg);
          } else {
            setShowFbModal(false);
          }
          return;
        }
        if (handled) return;

        const { code, state, error } = extractOAuthParams(popup);
        if (error) {
          handled = true;
          clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null;
          popup.close();
          setFbConnecting(false);
          const msg = 'Facebook API upgrade is in progress';
          setFbError(msg);
          setMetaApiAlert(msg);
          return;
        }
        if (code) {
          handled = true;
          clearInterval(fbPollTimerRef.current); fbPollTimerRef.current = null;
          popup.close();
          const result = await callFbCallback(code, state || 'facebook');
          await checkFbStatus();
          if (result.success) {
            setFbConnecting(false);
            setShowFbModal(false);
          } else {
            setFbConnecting(false);
            const msg = 'Facebook API upgrade is in progress';
            setFbError(msg);
            setMetaApiAlert(msg);
          }
        }
      }, 500);

    } catch (err) {
      dbg('handleFbOAuth error:', err.message);
      const msg = 'Facebook API upgrade is in progress';
      setFbError(msg);
      setMetaApiAlert(msg);
      setFbConnecting(false);
    }
  };

  const handleFbDisconnect = async () => {
    setFbDisconnecting(true);
    setFbError('');
    await apiCall('/facebook/disconnect', { method: 'POST' });
    setFbConnected(false);
    setFbAccount(null);
    setFbEvents(0);
    setFbLastSync('Never');
    setFbDisconnecting(false);
    setShowFbModal(false);
  };



  // ── Instagram OAuth handlers ──────────────────────────────────────────────
  const handleOpenIgModal  = () => { setIgError(''); setShowIgModal(true); };

  const handleCloseIgModal = () => {
    if (igPollTimerRef.current) { clearInterval(igPollTimerRef.current); igPollTimerRef.current = null; }
    if (igChannelRef.current)   { try { igChannelRef.current.close(); } catch {} igChannelRef.current = null; }
    if (igOauthPopupRef.current && !igOauthPopupRef.current.closed) igOauthPopupRef.current.close();
    igExchangingRef.current = false;
    setIgConnecting(false);
    setIgError('');
    setShowIgModal(false);
  };

  const handleIgOAuth = async () => {
    setIgConnecting(true);
    setIgError('');
    igExchangingRef.current = false;
    if (igPollTimerRef.current) clearInterval(igPollTimerRef.current);
    if (igChannelRef.current)   { try { igChannelRef.current.close(); } catch {} }

    try {
      const { ok, status, data, error: apiErr } = await apiCall('/instagram/login');
      dbg('IG /login status:', status);
      if (!ok) throw new Error(apiErr || `Server returned ${status}. Please try again.`);
      const url = data?.url;
      if (!url) throw new Error('No OAuth URL returned from server.');

      dbg('IG OAuth URL:', url);

      const popup = window.open(url, 'ig_oauth_popup', 'width=600,height=700,scrollbars=yes,resizable=yes');
      igOauthPopupRef.current = popup;
      if (!popup) throw new Error('Popup was blocked. Please allow popups for this site and try again.');
      dbg('IG popup opened');

      let handled = false;

      // Strategy A: BroadcastChannel — listens for message from callback page
      try {
        const ch = new BroadcastChannel('oauth_channel');
        igChannelRef.current = ch;
        ch.onmessage = async (event) => {
          dbg('BroadcastChannel IG message:', event.data);
          const platform = (event.data?.platform || '').toLowerCase();
          if (platform && platform !== 'instagram' && platform !== 'facebook') return;

          handled = true;
          ch.close(); igChannelRef.current = null;
          clearInterval(igPollTimerRef.current); igPollTimerRef.current = null;

          const { type, code, state } = event.data;
          if (type === 'OAUTH_SUCCESS' || type === 'oauth_success' || type === 'success') {
            if (code) {
              igExchangingRef.current = true;
              await callIgCallback(code, state || 'instagram');
              igExchangingRef.current = false;
            }
            await checkIgStatus();
            setIgConnecting(false);
            setShowIgModal(false);
          } else {
            setIgConnecting(false);
            setIgError(event.data?.error || 'Authorization failed. Please try again.');
          }
        };
      } catch (chErr) {
        dbg('BroadcastChannel (IG) not supported:', chErr.message);
      }

      // Strategy B: Poll the popup URL
      igPollTimerRef.current = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(igPollTimerRef.current); igPollTimerRef.current = null;
          if (handled) return;
          if (igChannelRef.current) { try { igChannelRef.current.close(); } catch {} igChannelRef.current = null; }

          dbg('IG popup closed — verifying status via API...');
          igExchangingRef.current = true;
          await new Promise(resolve => setTimeout(resolve, 1500));
          await checkIgStatus();
          igExchangingRef.current = false;

          setIgConnecting(false);
          if (!igConnected) {
            setIgError('Could not confirm connection. Please try again.');
          } else {
            setShowIgModal(false);
          }
          return;
        }
        if (handled || igExchangingRef.current) return;

        const { code, state, error } = extractOAuthParams(popup);
        if (error) {
          handled = true;
          clearInterval(igPollTimerRef.current); igPollTimerRef.current = null;
          popup.close();
          setIgConnecting(false);
          setIgError(`Authorization denied: ${error}`);
          return;
        }
        if (code) {
          handled = true;
          clearInterval(igPollTimerRef.current); igPollTimerRef.current = null;
          popup.close();
          igExchangingRef.current = true;
          await callIgCallback(code, state || 'instagram');
          igExchangingRef.current = false;
          await checkIgStatus();
          setIgConnecting(false);
          if (igConnected) {
            setShowIgModal(false);
          } else {
            dbg('IG callIgCallback failed, re-checking status...');
            setIgError('Connection failed. Please try again.');
          }
        }
      }, 500);

    } catch (err) {
      dbg('handleIgOAuth error:', err.message);
      setIgError(err.message || 'Failed to start OAuth. Please try again.');
      setIgConnecting(false);
    }
  };

  const handleIgDisconnect = async () => {
    setIgDisconnecting(true);
    setIgError('');
    await apiCall('/instagram/disconnect', { method: 'POST' });
    setIgConnected(false);
    setIgAccount(null);
    setIgEvents(0);
    setIgLastSync('Never');
    setIgDisconnecting(false);
    setShowIgModal(false);
  };



  // ── TikTok handlers ───────────────────────────────────────────────────────
  const handleOpenTiktokModal  = () => {
    setTiktokError('');
    setTiktokCopied(false);
    setShowTiktokModal(true);
  };

  const handleCloseTiktokModal = () => {
    setTiktokError('');
    setTiktokCopied(false);
    setTiktokConnecting(false);
    setShowTiktokModal(false);
  };

  const handleTiktokDisconnect = async () => {
    setTiktokDisconnecting(true);
    setTiktokError('');
    const { ok, status, data, error } = await apiCall('/tiktok/disconnect', { method: 'POST' });
    console.log('[API] POST /tiktok/disconnect →', { httpStatus: status, ok, body: data });
    if (!ok) {
      setTiktokError(error || data?.detail || data?.message || 'Failed to disconnect TikTok. Please try again.');
      setTiktokDisconnecting(false);
      return;
    }
    setTiktokConnected(false);
    setTiktokAccount(null);
    setTiktokEvents(0);
    setTiktokLastSync('Never');
    setTiktokDisconnecting(false);
    setShowTiktokModal(false);
  };

  const handleTiktokRefresh = async () => {
    setTiktokError('');
    await checkTiktokStatus();
  };

  const handleTiktokConnectApi = async (e) => {
    e?.preventDefault();
    if (!tiktokAccessToken.trim() && !tiktokPixelId.trim()) {
      setTiktokError('Please enter a TikTok Access Token or Pixel ID.');
      return;
    }
    setTiktokConnecting(true);
    setTiktokError('');
    try {
      const { ok, data, error } = await apiCall('/tiktok/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: tiktokAccessToken.trim(),
          pixel_id: tiktokPixelId.trim(),
          app_id: tiktokAppId.trim(),
        })
      });
      if (!ok) throw new Error(error || 'Failed to connect TikTok API.');
      setTiktokConnected(true);
      setTiktokAccount(data?.account || { name: 'TikTok Connected' });
      await checkTiktokStatus();
    } catch (err) {
      setTiktokError(err.message || 'Connection failed. Please verify your TikTok credentials.');
    } finally {
      setTiktokConnecting(false);
    }
  };

  const handleCopyTiktokWebhook = () => {
    const webhookUrl = getApiUrl('/webhooks/tiktok');
    navigator.clipboard.writeText(webhookUrl).then(() => {
      setTiktokCopied(true);
      setTimeout(() => setTiktokCopied(false), 2500);
    });
  };



  // ── Icon components ───────────────────────────────────────────────────────
  const ManyChatIcon = ({ size = 48 }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect width="48" height="48" rx="10" fill="#00B2FF" />
      <image
        href="https://abduzeedo.com/sites/default/files/styles/max_2600x2600/public/originals/hero_manychat.png"
        x="6" y="6" width="36" height="36"
      />
    </svg>
  );

  const InstagramIcon = ({ size = 28, style }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={style}>
      <defs>
        <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
          <stop offset="0%"  stopColor="#fdf497" />
          <stop offset="5%"  stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect width="48" height="48" rx="12" fill="url(#ig-grad)" />
      <rect x="13" y="13" width="22" height="22" rx="6" stroke="#fff" strokeWidth="2.5" fill="none" />
      <circle cx="24" cy="24" r="5.5" stroke="#fff" strokeWidth="2.5" fill="none" />
      <circle cx="33.5" cy="14.5" r="1.5" fill="#fff" />
    </svg>
  );



  // ── Platform config ───────────────────────────────────────────────────────
  const platforms = [
    {
      name: 'ManyChat',
      icon: ManyChatIcon,
      status: manyChatConnected ? 'connected' : 'disconnected',
      eventsCount: manyChatEvents,
      lastSync: manyChatConnected ? manyChatLastSync : 'Never',
      color: '#00B2FF', clickable: true, comingSoon: false,
      onCardClick: handleOpenModal,
    },
    {
      name: 'Facebook',
      icon: Facebook,
      status: fbConnected ? 'connected' : 'disconnected',
      eventsCount: fbEvents,
      lastSync: fbConnected ? fbLastSync : 'Never',
      color: '#1877F2', comingSoon: false, clickable: true,
      onCardClick: handleOpenFbModal,
    },
    {
      name: 'Instagram',
      icon: ({ size = 28, style }) => <InstagramIcon size={size} style={style} />,
      status: igConnected ? 'connected' : 'disconnected',
      eventsCount: igEvents,
      lastSync: igConnected ? igLastSync : 'Never',
      color: '#d6249f', comingSoon: false, clickable: true,
      onCardClick: handleOpenIgModal,
    },
    {
      name: 'Shopify',
      icon: ShoppingBag,
      status: shopifyConnected ? 'connected' : 'disconnected',
      eventsCount: shopifyEvents,
      lastSync: shopifyConnected ? shopifyLastSync : 'Never',
      color: '#95BF47', comingSoon: false, clickable: true,
      onCardClick: handleOpenShopifyModal,
    },
    {
      // TikTok now reflects live /tiktok/status data instead of a hardcoded
      // "Coming Soon" placeholder. No documented OAuth /login endpoint exists yet,
      // so the modal only shows status + a disconnect action.
      name: 'TikTok',
      icon: Music2,
      status: tiktokConnected ? 'connected' : 'disconnected',
      eventsCount: tiktokEvents,
      lastSync: tiktokConnected ? tiktokLastSync : 'Never',
      color: '#00f2ea', comingSoon: false, clickable: true,
      onCardClick: handleOpenTiktokModal,
    },
    {
      name: 'WooCommerce',
      icon: Store,
      status: wooConnected ? 'connected' : 'disconnected',
      eventsCount: wooEvents,
      lastSync: wooConnected ? wooLastSync : 'Never',
      color: '#7F54B3', comingSoon: false, clickable: true,
      onCardClick: handleOpenWooModal,
    },
    /*
    {
      // Google Analytics preserved - to show on UI, remove comment and set hidden: false
      name: 'Google Analytics',
      icon: BarChart4,
      status: 'disconnected', eventsCount: 0, lastSync: 'Never',
      color: '#F4B400', comingSoon: true, hidden: true,
    },
    */
  ];

  const filteredPlatforms = platforms.filter(p =>
    !p.hidden && p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderCard = (platform) => (
    <div
      className={`integration-card ${platform.clickable ? 'integration-card-clickable' : ''}`}
      onClick={() => platform.clickable && !platform.comingSoon && platform.onCardClick?.()}
      style={{ height: '100%' }}
    >
      <div className="integration-card-glow" style={{ backgroundColor: platform.color }} />
      <div className="integration-card-header">
        <div className="integration-card-header-left">
          <div
            className="integration-platform-icon"
            style={{ backgroundColor: `${platform.color}20`, borderColor: `${platform.color}40` }}
          >
            <platform.icon size={28} style={{ color: platform.color }} />
          </div>
          <div className="integration-platform-info">
            <h3 className="integration-platform-name">{platform.name}</h3>
            <div className="integration-status-indicator">
              {platform.name === 'Facebook' && metaApiAlert && !fbConnected ? (
                <>
                  <span className="status-dot" style={{ backgroundColor: '#f87171', boxShadow: '0 0 8px rgba(248, 113, 113, 0.5)' }} />
                  <span className="status-text" style={{ color: '#f87171' }}>API Upgrade</span>
                </>
              ) : (
                <>
                  <span className={`status-dot status-${platform.status}`} />
                  <span className={`status-text status-${platform.status}`}>{platform.status}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <button className="integration-external-btn" onClick={(e) => e.stopPropagation()}>
          <ExternalLink size={18} />
        </button>
      </div>
      <div className="integration-card-body">
        <div className="integration-events-card">
          <div className="integration-events-header">
            <span className="integration-events-label">Events Tracked</span>
            <span className="integration-events-count">{platform.eventsCount}</span>
          </div>
          <div className="integration-events-progress-track">
            <div
              className="integration-events-progress-bar"
              style={{
                width: `${Math.min(platform.eventsCount * 8, 100)}%`,
                background: `linear-gradient(to right, ${platform.color}, ${platform.color}CC)`,
                boxShadow: `0 0 10px ${platform.color}50`,
              }}
            />
          </div>
        </div>
        <div className="integration-sync-info">
          <span className="sync-info-label">Last Synced</span>
          <span className="sync-info-value">
            <RefreshCw size={10} className={platform.status === 'syncing' ? 'sync-icon syncing' : 'sync-icon'} />
            {platform.lastSync}
          </span>
        </div>
      </div>
      <div className="integration-card-footer">
        <button
          className={`integration-action-btn ${platform.status === 'disconnected' ? 'primary' : 'secondary'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (platform.clickable && !platform.comingSoon) platform.onCardClick?.();
          }}
        >
          {platform.status === 'disconnected' ? 'Initialize Connection' : 'Manage Connection'}
        </button>
      </div>
    </div>
  );



  return (
    <>
      <Helmet>
        <title>Integrations - Revexia AI Revenue Brain</title>
        <meta name="description" content="Platform integrations and data sync management" />
      </Helmet>

      <style>{`
        @keyframes intPageEntrance {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes intToastUp {
          from { opacity: 0; transform: translateX(-50%) translateY(24px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes mcSpin { to { transform: rotate(360deg); } }
        .mc-spinner { animation: mcSpin 0.8s linear infinite; }
      `}</style>

      {showToast && !manyChatConnected && (
        <LeaveToast onDismiss={() => setShowToast(false)} />
      )}

      <div
        className="integrations-page-wrapper"
        style={{
          opacity:    visible ? 1 : 0,
          transform:  visible ? 'translateY(0)' : 'translateY(18px)',
          transition: 'opacity 0.55s cubic-bezier(0.16,1,0.3,1), transform 0.55s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        <div className="container-fluid px-4">

          {/* Search and Filters */}
          <section className="integrations-search-section">
            <div className="search-input-wrapper">
              <Search className="search-input-icon" size={18} />
              <input
                type="text"
                placeholder="Search integrations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-field"
              />
            </div>
            <div className="search-actions">
              <button className="filter-platforms-btn"><Filter size={16} /> Filter Platforms</button>
              <div className="gateway-status-badge">
                <div className="gateway-pulse" />
                <span className="gateway-text">Gateway Optimal</span>
              </div>
            </div>
          </section>

          {/* Meta / Facebook API Alert Banner */}
          {metaApiAlert && (
            <div
              className="meta-api-alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '0.9rem 1.25rem',
                marginBottom: '1.75rem',
                borderRadius: '12px',
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                fontSize: '0.875rem',
                fontWeight: 600,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
                backdropFilter: 'blur(8px)',
                animation: 'intToastUp 0.3s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertCircle size={18} style={{ color: '#ef4444', flexShrink: 0 }} />
                <span>{metaApiAlert}</span>
              </div>
              <button
                onClick={() => setMetaApiAlert('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '6px',
                  transition: 'opacity 0.2s',
                }}
                aria-label="Dismiss alert"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* Integration Cards Grid */}
          <section className="row g-4 mb-5">
            {filteredPlatforms.map((platform) => (
              <div key={platform.name} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                {platform.comingSoon
                  ? <ComingSoonCard>{renderCard(platform)}</ComingSoonCard>
                  : renderCard(platform)
                }
              </div>
            ))}
            <div className="col-12 col-sm-6 col-lg-4 col-xl-3">
              <ComingSoonCard>
                <button className="add-integration-card" style={{ width: '100%' }}>
                  <div className="add-integration-icon"><Plus size={32} /></div>
                  <div className="add-integration-content">
                    <h3 className="add-integration-title">Request Custom Bridge</h3>
                    <p className="add-integration-text">
                      Need a platform not listed? Our team can build a custom API bridge for you.
                    </p>
                  </div>
                </button>
              </ComingSoonCard>
            </div>
          </section>

          {/* Footer Info */}
          <section className="security-info-section">
            <div className="security-info-card">
              <div className="security-info-left">
                <div className="security-info-icon"><AlertCircle size={32} /></div>
                <div className="security-info-content">
                  <h4 className="security-info-title">Security & API Protocols</h4>
                  <p className="security-info-text">
                    All platform integrations use 256-bit AES encryption and OAuth 2.0. Revexia
                    never stores raw credentials, only temporary access tokens with scoped permissions.
                  </p>
                </div>
              </div>
              <button className="security-docs-btn">View API Documentation</button>
            </div>
          </section>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          ManyChat Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showManyChatModal && (
        <div className="mc-modal-overlay" onClick={handleCloseModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon"><ManyChatIcon size={40} /></div>
                <div>
                  <h3 className="mc-modal-title">ManyChat Integration</h3>
                  <p className="mc-modal-subtitle">
                    {manyChatConnected && manyChatAccount
                      ? <>✅ Connected as <strong>{manyChatAccount.name}</strong></>
                      : 'Connect your ManyChat account via API key'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseModal}><X size={20} /></button>
            </div>

            {step === 'form' && (
              <form onSubmit={handleManyChatSubmit} className="mc-modal-body">
                <div className="mc-info-banner">
                  <Shield size={14} />
                  <span>Your API key is encrypted and stored securely. We never share your credentials.</span>
                </div>
                <div className="mc-field">
                  <label className="mc-label">
                    <Key size={13} className="mc-label-icon" />
                    ManyChat API Key <span className="mc-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="260049122536720:fead6e2998d556a8a8812b7a8..."
                    className="mc-input"
                    required
                    autoFocus
                  />
                  <p className="mc-field-hint">Find your API key in ManyChat → Settings → API</p>
                </div>
                {saveError && (
                  <div className="mc-alert mc-alert-error">
                    <AlertCircle size={15} /><span>{saveError}</span>
                  </div>
                )}
                <div className="mc-modal-footer">
                  <button type="button" className="mc-btn-cancel" onClick={handleCloseModal} disabled={isSaving}>Cancel</button>
                  <button type="submit" className="mc-btn-save" disabled={isSaving || !apiKey.trim()}>
                    {isSaving
                      ? <><Loader2 size={15} className="mc-spinner" /> Connecting...</>
                      : <><Key size={15} /> Save & Connect</>}
                  </button>
                </div>
              </form>
            )}

            {step === 'success' && accountData && (
              <div className="mc-modal-body">
                <div className="mc-success-banner">
                  <CheckCircle2 size={16} /><span>ManyChat account connected successfully!</span>
                </div>
                <div className="mc-account-card">
                  <div className="mc-account-avatar">
                    {accountData.avatar_link
                      ? <img src={accountData.avatar_link} alt={accountData.name} />
                      : <User size={28} />}
                  </div>
                  <div className="mc-account-info">
                    <div className="mc-account-name-row">
                      <h4 className="mc-account-name">{accountData.name}</h4>
                      {accountData.is_pro && <span className="mc-pro-badge"><Crown size={10} /> PRO</span>}
                    </div>
                    <p className="mc-account-category">{accountData.category}</p>
                    <p className="mc-account-username">@{accountData.username}</p>
                  </div>
                </div>
                <div className="mc-account-details">
                  {[
                    { icon: Globe,  label: 'Timezone',   value: accountData.timezone },
                    { icon: User,   label: 'Account ID', value: accountData.id },
                    { icon: Shield, label: 'Plan',       value: accountData.is_pro ? 'ManyChat Pro' : 'Free' },
                  ].map(({ icon: Icon, label, value }) => (
                    <div className="mc-detail-row" key={label}>
                      <span className="mc-detail-label"><Icon size={12} /> {label}</span>
                      <span className="mc-detail-value">{value}</span>
                    </div>
                  ))}
                </div>
                <div className="mc-info-banner" style={{ marginTop: '0.75rem' }}>
                  <Info size={14} />
                  <span>
                    Since ManyChat doesn't send Revexia real-time webhook events, the events count above
                    reflects your total tracked conversations rather than ManyChat-specific activity.
                  </span>
                </div>
                <div className="mc-modal-footer">
                  <button type="button" className="mc-btn-disconnect" onClick={handleDisconnect}>Disconnect</button>
                  <button type="button" className="mc-btn-save" onClick={handleCloseModal}>
                    <CheckCircle2 size={15} /> Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Facebook Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showFbModal && (
        <div className="mc-modal-overlay" onClick={handleCloseFbModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon">
                  <Facebook size={40} style={{ color: '#1877F2' }} />
                </div>
                <div>
                  <h3 className="mc-modal-title">Facebook Integration</h3>
                  <p className="mc-modal-subtitle">
                    {fbConnected
                      ? <>✅ {fbAccount?.page ? <>Connected as <strong>{fbAccount.page}</strong></> : 'Connected'}</>
                      : 'Connect your Facebook Page via OAuth'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseFbModal}><X size={20} /></button>
            </div>

            <div className="mc-modal-body">
              {fbConnected && (
                <div className="mc-success-banner" style={{ marginBottom: '1rem' }}>
                  <CheckCircle2 size={16} />
                  <span>Facebook connected successfully{fbAccount?.page ? ` as ${fbAccount.page}` : ''}!</span>
                </div>
              )}
              <div className="mc-info-banner" style={{ marginBottom: '1.25rem' }}>
                <Shield size={14} />
                <span>We only request <strong>pages_messaging</strong> and <strong>pages_read_engagement</strong> permissions. No posting access.</span>
              </div>
              {fbConnecting && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '0.6rem', padding: '1rem',
                  background: 'rgba(24,119,242,0.08)',
                  border: '1px solid rgba(24,119,242,0.2)',
                  borderRadius: '8px', marginBottom: '1rem',
                  fontSize: '0.82rem', color: '#93c5fd',
                }}>
                  <Loader2 size={15} className="mc-spinner" />
                  <span>Waiting for authorization… Complete the popup then wait a moment.</span>
                </div>
              )}
              <div className="mc-field">
                <button
                  type="button"
                  disabled={fbConnecting || fbDisconnecting}
                  onClick={handleFbOAuth}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '0.5rem', padding: '0.75rem 1.25rem',
                    background: '#1877F2', color: '#fff',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '0.875rem', fontWeight: 700,
                    opacity: (fbConnecting || fbDisconnecting) ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {fbConnecting
                    ? <><Loader2 size={16} className="mc-spinner" /> Verifying connection...</>
                    : <><Facebook size={16} /> {fbConnected ? 'Reconnect Facebook Page' : 'Connect Facebook Page'}</>
                  }
                </button>
              </div>
              {(fbError || metaApiAlert) && (
                <div className="mc-alert mc-alert-error" style={{ marginTop: '0.75rem' }}>
                  <AlertCircle size={15} /><span>{fbError || metaApiAlert}</span>
                </div>
              )}
              <div className="mc-modal-footer">
                {fbConnected && (
                  <button
                    type="button"
                    className="mc-btn-disconnect"
                    onClick={handleFbDisconnect}
                    disabled={fbDisconnecting || fbConnecting}
                  >
                    {fbDisconnecting
                      ? <><Loader2 size={14} className="mc-spinner" /> Disconnecting...</>
                      : 'Disconnect'}
                  </button>
                )}
                <button
                  type="button"
                  className="mc-btn-cancel"
                  onClick={handleCloseFbModal}
                  disabled={fbDisconnecting}
                >
                  {fbConnected ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Instagram Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showIgModal && (
        <div className="mc-modal-overlay" onClick={handleCloseIgModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon"><InstagramIcon size={40} /></div>
                <div>
                  <h3 className="mc-modal-title">Instagram Integration</h3>
                  <p className="mc-modal-subtitle">
                    {igConnected
                      ? <>✅ {igAccount?.page ? <>Connected as <strong>{igAccount.page}</strong></> : 'Connected'}</>
                      : 'Connect your Instagram Business account via OAuth'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseIgModal}><X size={20} /></button>
            </div>

            <div className="mc-modal-body">
              {igConnected && (
                <div className="mc-success-banner" style={{ marginBottom: '1rem' }}>
                  <CheckCircle2 size={16} />
                  <span>Instagram connected successfully{igAccount?.page ? ` as @${igAccount.page}` : ''}!</span>
                </div>
              )}
              <div className="mc-info-banner" style={{ marginBottom: '1.25rem' }}>
                <Shield size={14} />
                <span>
                  Requires an <strong>Instagram Business</strong> or <strong>Creator</strong> account
                  linked to a Facebook Page. We request{' '}
                  <strong>instagram_business_manage_messages</strong> permissions only.
                </span>
              </div>
              {igConnecting && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '0.6rem', padding: '1rem',
                  background: 'rgba(214,36,159,0.08)',
                  border: '1px solid rgba(214,36,159,0.2)',
                  borderRadius: '8px', marginBottom: '1rem',
                  fontSize: '0.82rem', color: '#f0abdc',
                }}>
                  <Loader2 size={15} className="mc-spinner" />
                  <span>Waiting for authorization… Complete the popup then wait a moment.</span>
                </div>
              )}
              <div className="mc-field">
                <button
                  type="button"
                  disabled={igConnecting || igDisconnecting}
                  onClick={handleIgOAuth}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '0.5rem', padding: '0.75rem 1.25rem',
                    background: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                    color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '0.875rem', fontWeight: 700,
                    opacity: (igConnecting || igDisconnecting) ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {igConnecting
                    ? <><Loader2 size={16} className="mc-spinner" /> Verifying connection...</>
                    : <><InstagramIcon size={16} /> {igConnected ? 'Reconnect Instagram' : 'Connect Instagram'}</>
                  }
                </button>
              </div>
              {igError && (
                <div className="mc-alert mc-alert-error" style={{ marginTop: '0.75rem' }}>
                  <AlertCircle size={15} /><span>{igError}</span>
                </div>
              )}
              <div className="mc-modal-footer">
                {igConnected && (
                  <button
                    type="button"
                    className="mc-btn-disconnect"
                    onClick={handleIgDisconnect}
                    disabled={igDisconnecting || igConnecting}
                  >
                    {igDisconnecting
                      ? <><Loader2 size={14} className="mc-spinner" /> Disconnecting...</>
                      : 'Disconnect'}
                  </button>
                )}
                <button
                  type="button"
                  className="mc-btn-cancel"
                  onClick={handleCloseIgModal}
                  disabled={igDisconnecting}
                >
                  {igConnected ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Shopify Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showShopifyModal && (
        <div className="mc-modal-overlay" onClick={handleCloseShopifyModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon">
                  <ShoppingBag size={40} style={{ color: '#95BF47' }} />
                </div>
                <div>
                  <h3 className="mc-modal-title">Shopify Integration</h3>
                  <p className="mc-modal-subtitle">
                    {shopifyConnected && shopifyAccount
                      ? <>✅ Connected — <strong>{shopifyAccount.domain?.replace('https://', '').replace('/', '') || 'Store active'}</strong></>
                      : 'Install the tracking snippet on your Shopify store'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseShopifyModal}><X size={20} /></button>
            </div>

            <div className="mc-modal-body">

              {/* ── Success state ── */}
              {shopifyStep === 'success' && (
                <>
                  <div className="mc-success-banner" style={{ marginBottom: '1rem' }}>
                    <CheckCircle2 size={16} />
                    <span>Shopify tracker is active and collecting events!</span>
                  </div>

                  {/* Store details */}
                  {shopifyAccount && (
                    <div className="mc-account-card" style={{ marginBottom: '1rem' }}>
                      <div className="mc-account-avatar" style={{
                        background: 'rgba(149,191,71,0.12)', border: '1px solid rgba(149,191,71,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 48, height: 48, borderRadius: 10,
                      }}>
                        <ShoppingBag size={22} style={{ color: '#95BF47' }} />
                      </div>
                      <div className="mc-account-info">
                        <div className="mc-account-name-row">
                          <h4 className="mc-account-name">{shopifyAccount.domain?.replace('https://', '').replace('/', '') || 'Shopify Store'}</h4>
                          <span style={{ fontSize: '0.6rem', fontWeight: 800, background: 'rgba(149,191,71,0.15)', color: '#95BF47', border: '1px solid rgba(149,191,71,0.3)', borderRadius: 999, padding: '0.15rem 0.5rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Active</span>
                        </div>
                        <p className="mc-account-username">Site ID: {shopifyAccount.siteId}</p>
                      </div>
                    </div>
                  )}

                  <div className="mc-account-details">
                    {[
                      { icon: Activity,     label: 'Events Tracked', value: String(shopifyEvents) },
                      { icon: RefreshCw,    label: 'Last Synced',    value: shopifyLastSync },
                    ].map(({ icon: Icon, label, value }) => (
                      <div className="mc-detail-row" key={label}>
                        <span className="mc-detail-label"><Icon size={12} /> {label}</span>
                        <span className="mc-detail-value">{value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Show snippet for reference */}
                  {(shopifySnippet || shopifySiteId) && (
                    <div style={{ marginTop: '1rem' }}>
                      <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Installed Snippet</p>
                      <div style={{
                        background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 8, padding: '0.75rem', fontFamily: 'monospace',
                        fontSize: '0.65rem', color: '#6EE7B7', lineHeight: 1.7,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 120, overflowY: 'auto',
                      }}>
                        {shopifySnippet || buildSnippet(shopifySiteId)}
                      </div>
                    </div>
                  )}

                  <div className="mc-modal-footer" style={{ marginTop: '1.25rem' }}>
                    <button
                      type="button"
                      className="mc-btn-disconnect"
                      onClick={handleShopifyDisconnect}
                      disabled={shopifyDisconnecting}
                    >
                      {shopifyDisconnecting
                        ? <><Loader2 size={14} className="mc-spinner" /> Disconnecting...</>
                        : 'Disconnect'}
                    </button>
                    <button
                      type="button"
                      className="mc-btn-cancel"
                      onClick={handleCloseShopifyModal}
                      disabled={shopifyDisconnecting}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}

              {/* ── Install state ── */}
              {shopifyStep === 'install' && (
                <>
                  <div className="mc-info-banner" style={{ marginBottom: '1.25rem' }}>
                    <Shield size={14} />
                    <span>Paste this snippet into your Shopify theme's <strong>theme.liquid</strong> file, just before the closing <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0 4px', borderRadius: 3 }}>&lt;/body&gt;</code> tag.</span>
                  </div>

                  <div className="mc-field" style={{ marginBottom: '1rem' }}>
                    <label className="mc-label">
                      <Globe size={13} className="mc-label-icon" style={{ color: '#95BF47' }} />
                      Shopify Store Domain (Optional)
                    </label>
                    <input
                      type="text"
                      value={shopifyDomain}
                      onChange={(e) => setShopifyDomain(e.target.value)}
                      placeholder="e.g. your-store.myshopify.com"
                      className="mc-input"
                    />
                    <p className="mc-field-hint">Specify your store domain to link incoming orders and session tracking.</p>
                  </div>

                  {/* Step 1: Generate ID */}
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Step 1 — Your Tracking Snippet
                    </p>

                    {shopifyGenerating ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1.5rem', background: 'rgba(149,191,71,0.05)', border: '1px solid rgba(149,191,71,0.15)', borderRadius: 8 }}>
                        <Loader2 size={15} className="mc-spinner" style={{ color: '#95BF47' }} />
                        <span style={{ fontSize: '0.77rem', color: '#95BF47' }}>Generating your unique Site ID…</span>
                      </div>
                    ) : shopifySiteId ? (
                      <>
                        <div style={{
                          background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(149,191,71,0.2)',
                          borderRadius: 8, padding: '0.85rem',
                          fontFamily: 'monospace', fontSize: '0.64rem', color: '#6EE7B7',
                          lineHeight: 1.75, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                          maxHeight: 160, overflowY: 'auto', marginBottom: '0.6rem',
                        }}>
                          {shopifySnippet || buildSnippet(shopifySiteId)}
                        </div>
                        <button
                          onClick={handleShopifyCopySnippet}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '0.45rem', padding: '0.6rem 1rem',
                            background: shopifyCopied ? 'rgba(16,185,129,0.15)' : 'rgba(149,191,71,0.1)',
                            border: `1px solid ${shopifyCopied ? 'rgba(16,185,129,0.35)' : 'rgba(149,191,71,0.3)'}`,
                            borderRadius: 8, color: shopifyCopied ? '#6EE7B7' : '#95BF47',
                            fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                          }}
                        >
                          {shopifyCopied
                            ? <><CheckCircle2 size={14} /> Copied to clipboard!</>
                            : <><Key size={14} /> Copy Snippet</>}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleShopifyGenerateId}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: '0.45rem', padding: '0.65rem 1rem',
                          background: 'rgba(149,191,71,0.1)', border: '1px solid rgba(149,191,71,0.3)',
                          borderRadius: 8, color: '#95BF47', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        <Zap size={14} /> Generate Site ID & Snippet
                      </button>
                    )}
                  </div>

                  {/* Step 2: Instructions */}
                  {shopifySiteId && (
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Step 2 — Install in Shopify
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                        {[
                          'Go to your Shopify Admin → Online Store → Themes',
                          'Click Actions → Edit Code on your active theme',
                          'Open Layout → theme.liquid',
                          'Paste the snippet just before </body> and click Save',
                        ].map((step, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                            <span style={{
                              width: 18, height: 18, borderRadius: '50%', background: 'rgba(149,191,71,0.15)',
                              border: '1px solid rgba(149,191,71,0.3)', color: '#95BF47',
                              fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center',
                              justifyContent: 'center', flexShrink: 0, marginTop: 1,
                            }}>{i + 1}</span>
                            <span style={{ fontSize: '0.74rem', color: '#9CA3AF', lineHeight: 1.5 }}>{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {shopifyError && (
                    <div className="mc-alert mc-alert-error" style={{ marginBottom: '0.75rem' }}>
                      <AlertCircle size={15} /><span>{shopifyError}</span>
                    </div>
                  )}

                  <div className="mc-modal-footer">
                    <button type="button" className="mc-btn-cancel" onClick={handleCloseShopifyModal}>Cancel</button>
                    {shopifySiteId && (
                      <button
                        type="button"
                        className="mc-btn-save"
                        onClick={handleShopifyVerify}
                        disabled={shopifyVerifying}
                      >
                        {shopifyVerifying
                          ? <><Loader2 size={15} className="mc-spinner" /> Verifying…</>
                          : <><CheckCircle2 size={15} /> I've Installed It</>}
                      </button>
                    )}
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TikTok Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showTiktokModal && (
        <div className="mc-modal-overlay" onClick={handleCloseTiktokModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon" style={{ background: 'rgba(0, 242, 234, 0.12)', border: '1px solid rgba(0, 242, 234, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12 }}>
                  <Music2 size={26} style={{ color: '#00f2ea' }} />
                </div>
                <div>
                  <h3 className="mc-modal-title">TikTok Integration</h3>
                  <p className="mc-modal-subtitle">
                    {tiktokConnected ? '✅ Active & tracking real-time events' : 'Connect via Webhook or Direct Events API'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseTiktokModal}><X size={20} /></button>
            </div>

            <div className="mc-modal-body">
              {tiktokConnected && (
                <>
                  <div className="mc-success-banner" style={{ marginBottom: '1rem' }}>
                    <CheckCircle2 size={16} />
                    <span>TikTok integration is active and streaming events!</span>
                  </div>

                  <div className="mc-account-card" style={{ marginBottom: '1rem' }}>
                    <div className="mc-account-avatar" style={{
                      background: 'rgba(0, 242, 234, 0.12)', border: '1px solid rgba(0, 242, 234, 0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 48, height: 48, borderRadius: 10,
                    }}>
                      <Music2 size={22} style={{ color: '#00f2ea' }} />
                    </div>
                    <div className="mc-account-info">
                      <div className="mc-account-name-row">
                        <h4 className="mc-account-name">{tiktokAccount?.name || 'TikTok Business'}</h4>
                        <span style={{ fontSize: '0.6rem', fontWeight: 800, background: 'rgba(0, 242, 234, 0.15)', color: '#00f2ea', border: '1px solid rgba(0, 242, 234, 0.3)', borderRadius: 999, padding: '0.15rem 0.5rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Active</span>
                      </div>
                      <p className="mc-account-username">Live Webhook Receiver</p>
                    </div>
                  </div>

                  <div className="mc-account-details">
                    {[
                      { icon: Activity,  label: 'Events Tracked', value: String(tiktokEvents) },
                      { icon: RefreshCw, label: 'Last Synced',    value: tiktokLastSync },
                    ].map(({ icon: Icon, label, value }) => (
                      <div className="mc-detail-row" key={label}>
                        <span className="mc-detail-label"><Icon size={12} /> {label}</span>
                        <span className="mc-detail-value">{value}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Connected Webhook Endpoint</p>
                    <div style={{
                      background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(0, 242, 234, 0.2)',
                      borderRadius: 8, padding: '0.75rem', fontFamily: 'monospace',
                      fontSize: '0.7rem', color: '#00f2ea', lineHeight: 1.5,
                      wordBreak: 'break-all', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem'
                    }}>
                      <span>{getApiUrl('/webhooks/tiktok')}</span>
                      <button
                        onClick={handleCopyTiktokWebhook}
                        style={{
                          background: 'rgba(0, 242, 234, 0.15)', border: '1px solid rgba(0, 242, 234, 0.3)',
                          borderRadius: 6, color: '#00f2ea', padding: '4px 8px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.7rem'
                        }}
                      >
                        {tiktokCopied ? <Check size={12} /> : <Copy size={12} />}
                        {tiktokCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  {tiktokError && (
                    <div className="mc-alert mc-alert-error" style={{ marginTop: '0.75rem' }}>
                      <AlertCircle size={15} /><span>{tiktokError}</span>
                    </div>
                  )}

                  <div className="mc-modal-footer" style={{ marginTop: '1.25rem' }}>
                    <button
                      type="button"
                      className="mc-btn-disconnect"
                      onClick={handleTiktokDisconnect}
                      disabled={tiktokDisconnecting}
                    >
                      {tiktokDisconnecting
                        ? <><Loader2 size={14} className="mc-spinner" /> Disconnecting...</>
                        : 'Disconnect'}
                    </button>
                    <button
                      type="button"
                      className="mc-btn-cancel"
                      onClick={handleTiktokRefresh}
                      disabled={tiktokDisconnecting}
                    >
                      <RefreshCw size={14} /> Refresh
                    </button>
                    <button
                      type="button"
                      className="mc-btn-save"
                      onClick={handleCloseTiktokModal}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}

              {!tiktokConnected && (
                <>
                  {/* Mode switcher tabs */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', background: 'rgba(255,255,255,0.03)', padding: '4px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <button
                      type="button"
                      onClick={() => setTiktokTab('webhook')}
                      style={{
                        flex: 1, padding: '0.55rem 0.75rem', borderRadius: '8px', border: 'none',
                        background: tiktokTab === 'webhook' ? 'rgba(0, 242, 234, 0.15)' : 'transparent',
                        color: tiktokTab === 'webhook' ? '#00f2ea' : '#9CA3AF',
                        fontWeight: tiktokTab === 'webhook' ? 700 : 500, fontSize: '0.78rem',
                        cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}
                    >
                      <Globe size={13} /> Webhook Setup
                    </button>
                    <button
                      type="button"
                      onClick={() => setTiktokTab('api')}
                      style={{
                        flex: 1, padding: '0.55rem 0.75rem', borderRadius: '8px', border: 'none',
                        background: tiktokTab === 'api' ? 'rgba(0, 242, 234, 0.15)' : 'transparent',
                        color: tiktokTab === 'api' ? '#00f2ea' : '#9CA3AF',
                        fontWeight: tiktokTab === 'api' ? 700 : 500, fontSize: '0.78rem',
                        cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}
                    >
                      <Key size={13} /> TikTok API / Pixel
                    </button>
                  </div>

                  {tiktokTab === 'webhook' && (
                    <>
                      <div className="mc-info-banner" style={{ marginBottom: '1rem', background: 'rgba(0, 242, 234, 0.08)', borderColor: 'rgba(0, 242, 234, 0.2)', color: '#a5f3fc' }}>
                        <Info size={14} style={{ color: '#00f2ea' }} />
                        <span>TikTok events stream in real-time via webhook. Configure this destination in your TikTok Developer or Events portal.</span>
                      </div>

                      <div className="mc-field" style={{ marginBottom: '1rem' }}>
                        <label className="mc-label">
                          <LinkIcon size={13} className="mc-label-icon" style={{ color: '#00f2ea' }} />
                          Revexia TikTok Webhook URL
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input
                            type="text"
                            readOnly
                            value={getApiUrl('/webhooks/tiktok')}
                            className="mc-input"
                            style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#00f2ea' }}
                          />
                          <button
                            type="button"
                            onClick={handleCopyTiktokWebhook}
                            style={{
                              background: tiktokCopied ? 'rgba(16,185,129,0.15)' : 'rgba(0, 242, 234, 0.12)',
                              border: `1px solid ${tiktokCopied ? 'rgba(16,185,129,0.35)' : 'rgba(0, 242, 234, 0.3)'}`,
                              borderRadius: 10, color: tiktokCopied ? '#6EE7B7' : '#00f2ea',
                              padding: '0 1rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                            }}
                          >
                            {tiktokCopied ? <Check size={14} /> : <Copy size={14} />}
                            {tiktokCopied ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Setup Instructions
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                          {[
                            'Go to TikTok for Business → Events Manager or TikTok Developer Portal',
                            'Select Webhooks or Lead Generation integration settings',
                            'Paste the Revexia Webhook URL into the Webhook Destination field',
                            'Subscribe to order, visit, or lead event triggers and save',
                          ].map((step, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                              <span style={{
                                width: 18, height: 18, borderRadius: '50%', background: 'rgba(0, 242, 234, 0.15)',
                                border: '1px solid rgba(0, 242, 234, 0.3)', color: '#00f2ea',
                                fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center',
                                justifyContent: 'center', flexShrink: 0, marginTop: 1,
                              }}>{i + 1}</span>
                              <span style={{ fontSize: '0.74rem', color: '#9CA3AF', lineHeight: 1.5 }}>{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {tiktokError && (
                        <div className="mc-alert mc-alert-error" style={{ marginBottom: '0.75rem' }}>
                          <AlertCircle size={15} /><span>{tiktokError}</span>
                        </div>
                      )}

                      <div className="mc-modal-footer">
                        <button type="button" className="mc-btn-cancel" onClick={handleCloseTiktokModal}>Close</button>
                        <button
                          type="button"
                          className="mc-btn-save"
                          onClick={handleTiktokRefresh}
                          style={{ background: 'linear-gradient(135deg, #00f2ea, #0284c7)' }}
                        >
                          <RefreshCw size={14} /> Check Connection
                        </button>
                      </div>
                    </>
                  )}

                  {tiktokTab === 'api' && (
                    <form onSubmit={handleTiktokConnectApi}>
                      <div className="mc-info-banner" style={{ marginBottom: '1rem', background: 'rgba(0, 242, 234, 0.08)', borderColor: 'rgba(0, 242, 234, 0.2)', color: '#a5f3fc' }}>
                        <Shield size={14} style={{ color: '#00f2ea' }} />
                        <span>Connect your TikTok Events API / Pixel directly using an Access Token from Events Manager.</span>
                      </div>

                      <div className="mc-field" style={{ marginBottom: '0.85rem' }}>
                        <label className="mc-label">
                          <Code size={13} className="mc-label-icon" style={{ color: '#00f2ea' }} />
                          TikTok Pixel ID <span className="mc-required">*</span>
                        </label>
                        <input
                          type="text"
                          value={tiktokPixelId}
                          onChange={(e) => setTiktokPixelId(e.target.value)}
                          placeholder="e.g. C81234567890ABCDEFGH"
                          className="mc-input"
                          required
                        />
                      </div>

                      <div className="mc-field" style={{ marginBottom: '0.85rem' }}>
                        <label className="mc-label">
                          <Key size={13} className="mc-label-icon" style={{ color: '#00f2ea' }} />
                          Access Token <span className="mc-required">*</span>
                        </label>
                        <input
                          type="password"
                          value={tiktokAccessToken}
                          onChange={(e) => setTiktokAccessToken(e.target.value)}
                          placeholder="Long-lived TikTok Events API access token"
                          className="mc-input"
                          required
                        />
                      </div>

                      <div className="mc-field" style={{ marginBottom: '1rem' }}>
                        <label className="mc-label">
                          <Globe size={13} className="mc-label-icon" style={{ color: '#00f2ea' }} />
                          TikTok App ID (Optional)
                        </label>
                        <input
                          type="text"
                          value={tiktokAppId}
                          onChange={(e) => setTiktokAppId(e.target.value)}
                          placeholder="e.g. 7123456789012345678"
                          className="mc-input"
                        />
                      </div>

                      {tiktokError && (
                        <div className="mc-alert mc-alert-error" style={{ marginBottom: '0.75rem' }}>
                          <AlertCircle size={15} /><span>{tiktokError}</span>
                        </div>
                      )}

                      <div className="mc-modal-footer">
                        <button type="button" className="mc-btn-cancel" onClick={handleCloseTiktokModal} disabled={tiktokConnecting}>Cancel</button>
                        <button
                          type="submit"
                          className="mc-btn-save"
                          disabled={tiktokConnecting || (!tiktokPixelId.trim() && !tiktokAccessToken.trim())}
                          style={{ background: 'linear-gradient(135deg, #00f2ea, #0284c7)' }}
                        >
                          {tiktokConnecting ? <><Loader2 size={14} className="mc-spinner" /> Connecting...</> : <><CheckCircle2 size={14} /> Connect TikTok API</>}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          WooCommerce Modal
      ══════════════════════════════════════════════════════════════════════ */}
      {showWooModal && (
        <div className="mc-modal-overlay" onClick={handleCloseWooModal}>
          <div className="mc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="mc-modal-header">
              <div className="mc-modal-header-left">
                <div className="mc-modal-icon" style={{ background: 'rgba(127, 84, 179, 0.15)', border: '1px solid rgba(127, 84, 179, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12 }}>
                  <Store size={26} style={{ color: '#7F54B3' }} />
                </div>
                <div>
                  <h3 className="mc-modal-title">WooCommerce Integration</h3>
                  <p className="mc-modal-subtitle">
                    {wooConnected && wooAccount
                      ? <>✅ Connected — <strong>{wooAccount.domain?.replace('https://', '').replace('http://', '').replace('/', '') || 'Store active'}</strong></>
                      : 'Connect your WooCommerce store via Tracking Snippet or REST API'}
                  </p>
                </div>
              </div>
              <button className="mc-modal-close" onClick={handleCloseWooModal}><X size={20} /></button>
            </div>

            <div className="mc-modal-body">
              {/* ── Success state ── */}
              {(wooStep === 'success' || wooConnected) && (
                <>
                  <div className="mc-success-banner" style={{ marginBottom: '1rem', background: 'rgba(127, 84, 179, 0.12)', borderColor: 'rgba(127, 84, 179, 0.3)', color: '#d8b4fe' }}>
                    <CheckCircle2 size={16} style={{ color: '#c084fc' }} />
                    <span>WooCommerce store is connected and syncing events!</span>
                  </div>

                  {wooAccount && (
                    <div className="mc-account-card" style={{ marginBottom: '1rem' }}>
                      <div className="mc-account-avatar" style={{
                        background: 'rgba(127, 84, 179, 0.15)', border: '1px solid rgba(127, 84, 179, 0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 48, height: 48, borderRadius: 10,
                      }}>
                        <Store size={22} style={{ color: '#7F54B3' }} />
                      </div>
                      <div className="mc-account-info">
                        <div className="mc-account-name-row">
                          <h4 className="mc-account-name">{wooAccount.domain?.replace('https://', '').replace('http://', '').replace('/', '') || 'WooCommerce Store'}</h4>
                          <span style={{ fontSize: '0.6rem', fontWeight: 800, background: 'rgba(127, 84, 179, 0.18)', color: '#c084fc', border: '1px solid rgba(127, 84, 179, 0.35)', borderRadius: 999, padding: '0.15rem 0.5rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Active</span>
                        </div>
                        <p className="mc-account-username">{wooAccount.siteId ? `Site ID: ${wooAccount.siteId}` : 'WooCommerce REST API'}</p>
                      </div>
                    </div>
                  )}

                  <div className="mc-account-details">
                    {[
                      { icon: Activity,     label: 'Events Tracked', value: String(wooEvents) },
                      { icon: RefreshCw,    label: 'Last Synced',    value: wooLastSync },
                    ].map(({ icon: Icon, label, value }) => (
                      <div className="mc-detail-row" key={label}>
                        <span className="mc-detail-label"><Icon size={12} /> {label}</span>
                        <span className="mc-detail-value">{value}</span>
                      </div>
                    ))}
                  </div>

                  {(wooSnippet || wooSiteId) && (
                    <div style={{ marginTop: '1rem' }}>
                      <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Installed Tracking Code</p>
                      <div style={{
                        background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 8, padding: '0.75rem', fontFamily: 'monospace',
                        fontSize: '0.65rem', color: '#d8b4fe', lineHeight: 1.7,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 120, overflowY: 'auto',
                      }}>
                        {wooSnippet || buildWooSnippet(wooSiteId)}
                      </div>
                    </div>
                  )}

                  <div className="mc-modal-footer" style={{ marginTop: '1.25rem' }}>
                    <button
                      type="button"
                      className="mc-btn-disconnect"
                      onClick={handleWooDisconnect}
                      disabled={wooDisconnecting}
                    >
                      {wooDisconnecting
                        ? <><Loader2 size={14} className="mc-spinner" /> Disconnecting...</>
                        : 'Disconnect'}
                    </button>
                    <button
                      type="button"
                      className="mc-btn-cancel"
                      onClick={handleCloseWooModal}
                      disabled={wooDisconnecting}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}

              {/* ── Connect / Install states ── */}
              {wooStep !== 'success' && !wooConnected && (
                <>
                  {/* Mode switcher */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', background: 'rgba(255,255,255,0.03)', padding: '4px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <button
                      type="button"
                      onClick={() => setWooStep('snippet')}
                      style={{
                        flex: 1, padding: '0.55rem 0.75rem', borderRadius: '8px', border: 'none',
                        background: wooStep === 'snippet' ? 'rgba(127, 84, 179, 0.2)' : 'transparent',
                        color: wooStep === 'snippet' ? '#d8b4fe' : '#9CA3AF',
                        fontWeight: wooStep === 'snippet' ? 700 : 500, fontSize: '0.78rem',
                        cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}
                    >
                      <Code size={13} /> Tracking Snippet (Recommended)
                    </button>
                    <button
                      type="button"
                      onClick={() => setWooStep('api')}
                      style={{
                        flex: 1, padding: '0.55rem 0.75rem', borderRadius: '8px', border: 'none',
                        background: wooStep === 'api' ? 'rgba(127, 84, 179, 0.2)' : 'transparent',
                        color: wooStep === 'api' ? '#d8b4fe' : '#9CA3AF',
                        fontWeight: wooStep === 'api' ? 700 : 500, fontSize: '0.78rem',
                        cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}
                    >
                      <Key size={13} /> REST API Keys
                    </button>
                  </div>

                  {wooStep === 'snippet' && (
                    <>
                      <div className="mc-info-banner" style={{ marginBottom: '1.25rem', background: 'rgba(127, 84, 179, 0.1)', borderColor: 'rgba(127, 84, 179, 0.25)', color: '#d8b4fe' }}>
                        <Shield size={14} style={{ color: '#c084fc' }} />
                        <span>Add this snippet to your WordPress theme's <strong>header.php</strong> or using the free <strong>WPCode</strong> plugin.</span>
                      </div>

                      <div className="mc-field" style={{ marginBottom: '1rem' }}>
                        <label className="mc-label">
                          <Globe size={13} className="mc-label-icon" style={{ color: '#7F54B3' }} />
                          WooCommerce Store URL (Optional)
                        </label>
                        <input
                          type="text"
                          value={wooDomain}
                          onChange={(e) => setWooDomain(e.target.value)}
                          placeholder="e.g. https://my-woocommerce-store.com"
                          className="mc-input"
                        />
                      </div>

                      {/* Step 1: Snippet */}
                      <div style={{ marginBottom: '1rem' }}>
                        <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Step 1 — Your Tracking Snippet
                        </p>

                        {wooGenerating ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1.5rem', background: 'rgba(127, 84, 179, 0.05)', border: '1px solid rgba(127, 84, 179, 0.2)', borderRadius: 8 }}>
                            <Loader2 size={15} className="mc-spinner" style={{ color: '#7F54B3' }} />
                            <span style={{ fontSize: '0.77rem', color: '#d8b4fe' }}>Generating WooCommerce Site ID…</span>
                          </div>
                        ) : wooSiteId ? (
                          <>
                            <div style={{
                              background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(127, 84, 179, 0.25)',
                              borderRadius: 8, padding: '0.85rem',
                              fontFamily: 'monospace', fontSize: '0.64rem', color: '#d8b4fe',
                              lineHeight: 1.75, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                              maxHeight: 160, overflowY: 'auto', marginBottom: '0.6rem',
                            }}>
                              {wooSnippet || buildWooSnippet(wooSiteId)}
                            </div>
                            <button
                              onClick={handleWooCopySnippet}
                              style={{
                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                gap: '0.45rem', padding: '0.6rem 1rem',
                                background: wooCopied ? 'rgba(16,185,129,0.15)' : 'rgba(127, 84, 179, 0.12)',
                                border: `1px solid ${wooCopied ? 'rgba(16,185,129,0.35)' : 'rgba(127, 84, 179, 0.3)'}`,
                                borderRadius: 8, color: wooCopied ? '#6EE7B7' : '#d8b4fe',
                                fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                              }}
                            >
                              {wooCopied
                                ? <><CheckCircle2 size={14} /> Copied to clipboard!</>
                                : <><Copy size={14} /> Copy Snippet</>}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={handleWooGenerateId}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              gap: '0.45rem', padding: '0.65rem 1rem',
                              background: 'rgba(127, 84, 179, 0.15)', border: '1px solid rgba(127, 84, 179, 0.35)',
                              borderRadius: 8, color: '#d8b4fe', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                            }}
                          >
                            <Zap size={14} /> Generate WooCommerce Snippet
                          </button>
                        )}
                      </div>

                      {/* Step 2: WordPress instructions */}
                      {wooSiteId && (
                        <div style={{ marginBottom: '1rem' }}>
                          <p style={{ fontSize: '0.68rem', color: '#6B7280', marginBottom: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Step 2 — Install in WordPress / WooCommerce
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                            {[
                              'In WordPress Admin, go to Plugins → Add New → install WPCode (Insert Headers and Footers)',
                              'Navigate to Code Snippets → Header & Footer in your WordPress menu',
                              'Paste the tracking snippet into the Header textarea and click Save Changes',
                              'Return here and click "I\'ve Installed It" to verify',
                            ].map((step, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                                <span style={{
                                  width: 18, height: 18, borderRadius: '50%', background: 'rgba(127, 84, 179, 0.2)',
                                  border: '1px solid rgba(127, 84, 179, 0.4)', color: '#d8b4fe',
                                  fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center',
                                  justifyContent: 'center', flexShrink: 0, marginTop: 1,
                                }}>{i + 1}</span>
                                <span style={{ fontSize: '0.74rem', color: '#9CA3AF', lineHeight: 1.5 }}>{step}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {wooError && (
                        <div className="mc-alert mc-alert-error" style={{ marginBottom: '0.75rem' }}>
                          <AlertCircle size={15} /><span>{wooError}</span>
                        </div>
                      )}

                      <div className="mc-modal-footer">
                        <button type="button" className="mc-btn-cancel" onClick={handleCloseWooModal}>Cancel</button>
                        {wooSiteId && (
                          <button
                            type="button"
                            className="mc-btn-save"
                            onClick={handleWooVerifySnippet}
                            disabled={wooVerifying}
                            style={{ background: 'linear-gradient(135deg, #7F54B3, #9333ea)' }}
                          >
                            {wooVerifying
                              ? <><Loader2 size={15} className="mc-spinner" /> Verifying…</>
                              : <><CheckCircle2 size={15} /> I've Installed It</>}
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {wooStep === 'api' && (
                    <form onSubmit={handleWooConnectApi}>
                      <div className="mc-info-banner" style={{ marginBottom: '1.25rem', background: 'rgba(127, 84, 179, 0.1)', borderColor: 'rgba(127, 84, 179, 0.25)', color: '#d8b4fe' }}>
                        <Shield size={14} style={{ color: '#c084fc' }} />
                        <span>Generate Read/Write keys in WooCommerce → Settings → Advanced → REST API.</span>
                      </div>

                      <div className="mc-field" style={{ marginBottom: '0.85rem' }}>
                        <label className="mc-label">
                          <Globe size={13} className="mc-label-icon" style={{ color: '#7F54B3' }} />
                          Store URL <span className="mc-required">*</span>
                        </label>
                        <input
                          type="url"
                          value={wooDomain}
                          onChange={(e) => setWooDomain(e.target.value)}
                          placeholder="https://yourstore.com"
                          className="mc-input"
                          required
                        />
                      </div>

                      <div className="mc-field" style={{ marginBottom: '0.85rem' }}>
                        <label className="mc-label">
                          <Key size={13} className="mc-label-icon" style={{ color: '#7F54B3' }} />
                          Consumer Key <span className="mc-required">*</span>
                        </label>
                        <input
                          type="text"
                          value={wooConsumerKey}
                          onChange={(e) => setWooConsumerKey(e.target.value)}
                          placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="mc-input"
                          required
                        />
                      </div>

                      <div className="mc-field" style={{ marginBottom: '1rem' }}>
                        <label className="mc-label">
                          <Shield size={13} className="mc-label-icon" style={{ color: '#7F54B3' }} />
                          Consumer Secret <span className="mc-required">*</span>
                        </label>
                        <input
                          type="password"
                          value={wooConsumerSecret}
                          onChange={(e) => setWooConsumerSecret(e.target.value)}
                          placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="mc-input"
                          required
                        />
                      </div>

                      {wooError && (
                        <div className="mc-alert mc-alert-error" style={{ marginBottom: '0.75rem' }}>
                          <AlertCircle size={15} /><span>{wooError}</span>
                        </div>
                      )}

                      <div className="mc-modal-footer">
                        <button type="button" className="mc-btn-cancel" onClick={handleCloseWooModal} disabled={wooConnecting}>Cancel</button>
                        <button
                          type="submit"
                          className="mc-btn-save"
                          disabled={wooConnecting || !wooDomain.trim() || !wooConsumerKey.trim() || !wooConsumerSecret.trim()}
                          style={{ background: 'linear-gradient(135deg, #7F54B3, #9333ea)' }}
                        >
                          {wooConnecting
                            ? <><Loader2 size={15} className="mc-spinner" /> Connecting…</>
                            : <><Store size={15} /> Connect WooCommerce API</>}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Integrations;